// IllumiTrain Adaptive Engine — Public API Surface
// Multi-cert architecture: initStore (once) + initCertContext (per cert)

import { initStore } from './firebase-store.js';
import { createEvent } from './event-store.js';
import { gradeAnswer, getCardState, saveCardState, initCard, getRetrievability, logRecallValidation } from './scheduler.js';
import { updateAbility, getCat, loadTheta, saveTheta, getAllCatStates } from './selector.js';
import { updateMastery } from './mastery.js';
import { updateCalibration } from './calibration.js';
import { triggerPropagation } from './propagation.js';
import { checkFatigue } from './fatigue.js';
import { startSession, getNextQuestion, endSession, recordSessionAnswer } from './session-planner.js';
import { getLearnerProfile } from './learner-profile.js';
import { initGraph, getGraphStats } from './knowledge-graph.js';
import { DEFAULT_DOMAIN_WEIGHTS } from './constants.js';
import { trackAnswer, trackSession, flushAnalytics, getDistractorFrequency, getReinforcementRecoveryRate, getSessionAnalytics } from './analytics.js';
import { forecastWeaknesses, getReinforcementTarget } from './weakness-forecast.js';
import { recordDistractor, detectMisconceptions, getMisconceptionSummary } from './misconception.js';
import { getInsight, getQuestionExplanation, getMisconceptionExplanation, getReadinessInsight, setExplanationCert } from './explanations.js';

// ── Per-cert contexts ──
// { [certId]: { questionBank, questionIndex, initialized } }
const _certContexts = {};
let _storeInitialized = false;

/**
 * Initialize the Firebase store. Called once on app load.
 * @param {object} firebaseApp - initialized Firebase app instance
 */
export function initStoreOnce(firebaseApp) {
  if (_storeInitialized) return;
  initStore(firebaseApp);
  _storeInitialized = true;
}

/**
 * Initialize a cert context. Called per cert when switching or first loading.
 *
 * @param {string} certId - cert identifier (e.g., 'md102', 'az900')
 * @param {Array} questionBank - enriched question array for this cert
 * @param {object} certSpec - { domainWeights: { 1: w1, 2: w2, ... }, displayName: '...' }
 */
export function initCertContext(certId, questionBank, certSpec) {
  // Build index for O(1) lookup
  const questionIndex = {};
  for (const q of questionBank) {
    questionIndex[q.id] = q;
  }

  // Initialize knowledge graph for this cert
  initGraph(certId, questionBank);
  const graphStats = getGraphStats(certId);

  _certContexts[certId] = {
    questionBank: questionBank,
    questionIndex: questionIndex,
    certSpec: certSpec || { domainWeights: DEFAULT_DOMAIN_WEIGHTS },
    initialized: true
  };

  console.log('[Engine] Initialized cert "' + certId + '" with ' + questionBank.length + ' questions' +
    (graphStats ? ', graph: ' + graphStats.skills + ' skills, ' + graphStats.concepts + ' concepts, ' + graphStats.crossDomainSkills + ' cross-domain' : ''));
}

/**
 * Get a cert context. Returns null if not initialized.
 * @param {string} certId
 * @returns {object|null}
 */
export function getCertContext(certId) {
  return _certContexts[certId] || null;
}

/**
 * Check if a cert context is initialized.
 * @param {string} certId
 * @returns {boolean}
 */
export function isCertInitialized(certId) {
  return !!(_certContexts[certId] && _certContexts[certId].initialized);
}

/**
 * Record an answer — the core control loop.
 * Every answer triggers: event → FSRS → jsCAT → BKT/calibration/propagation/fatigue
 *
 * @param {string} userId
 * @param {string} certId
 * @param {string} sessionToken
 * @param {object} answerData - { questionId, correct, responseMs, confidence, explanationMs }
 * @returns {object} { cardState, masteryDeltas, calibrationUpdate, alerts, nextActionHints }
 */
export async function recordAnswer(userId, certId, sessionToken, answerData) {
  const ctx = _certContexts[certId];
  if (!ctx) throw new Error('Cert context not initialized: ' + certId);

  const { questionId, correct, responseMs, confidence, explanationMs } = answerData;

  // 1. Load question metadata (with safe defaults for un-enriched questions)
  const question = ctx.questionIndex[questionId];
  if (!question) throw new Error('Unknown questionId: ' + questionId);

  // Ensure enrichment fields have safe defaults
  if (!question.questionType) question.questionType = question.type || 'mcq';
  if (!question.complexity) question.complexity = 2;
  if (!question.expectedRtMs) question.expectedRtMs = 20000;
  if (question.irtDifficulty == null) question.irtDifficulty = 0.0;
  if (question.irtDiscrimination == null) question.irtDiscrimination = 1.0;
  if (!question.skills) question.skills = [];
  if (!question.conceptTags) question.conceptTags = [];
  if (!question.domain) question.domain = 1;

  // 2. Create canonical learning event
  const session = await import('./session-planner.js').then(m => m.getSessionState(sessionToken));
  const eventId = await createEvent(userId, certId, {
    questionId,
    correct,
    responseMs,
    confidence: confidence || 'unsure',
    explanationMs: explanationMs || 0,
    sessionPosition: session ? session.position : 0,
    sessionId: sessionToken,
    questionMeta: {
      questionType: question.questionType,
      complexity: question.complexity,
      domain: question.domain,
      skills: question.skills || [],
      conceptTags: question.conceptTags || []
    }
  });

  // 3. FSRS: grade answer and update card
  let card = await getCardState(userId, certId, questionId);
  const isDueCard = !!card; // card existed before — it's a review/due card
  if (!card) card = initCard();

  // Capture predicted recall before grading (for FSRS validation)
  const predictedRecall = isDueCard ? getRetrievability(card) : null;

  const { newCard, grade } = gradeAnswer(card, responseMs, question.expectedRtMs, correct);

  // 4. Save card state to Firebase
  await saveCardState(userId, certId, questionId, newCard);

  // 4b. Log FSRS recall validation for due cards (predicted vs actual)
  if (isDueCard && predictedRecall !== null) {
    logRecallValidation(userId, certId, questionId, predictedRecall, correct, newCard.stability)
      .catch(() => {}); // non-blocking
  }

  // 5. Update jsCAT ability for this domain
  const cat = getCat(certId, question.domain);
  updateAbility(cat, {
    questionId,
    irtDifficulty: question.irtDifficulty,
    irtDiscrimination: question.irtDiscrimination
  }, correct);

  // 6. Save theta to Firebase
  await saveTheta(userId, certId);

  // 7. BKT mastery update
  const masteryDeltas = await updateMastery(userId, certId, question.skills || [], correct);

  // 8. Confidence calibration (user-global, not cert-scoped)
  const calibrationUpdate = await updateCalibration(userId, confidence || 'unsure', correct);

  // 9. Concept propagation
  await triggerPropagation(userId, certId, questionId, question.conceptTags || [], correct);

  // 10. Misconception tracking (on wrong answers)
  if (!correct && answerData.selectedChoice) {
    recordDistractor(userId, certId, questionId, answerData.selectedChoice, question.skills, question.conceptTags)
      .catch(() => {}); // non-blocking
  }

  // 11. Fatigue check
  const fatigueAlert = await checkFatigue(userId, sessionToken);

  // 11. Update session state (includes question type for diversity tracking)
  recordSessionAnswer(certId, sessionToken, questionId, correct, question.domain, responseMs, question.questionType);

  // 12. Analytics tracking (buffered, non-blocking)
  trackAnswer(userId, certId, {
    questionId,
    sessionId: sessionToken,
    correct,
    responseMs,
    selectedChoice: answerData.selectedChoice || null,
    correctChoice: answerData.correctChoice || null,
    distractorChosen: !correct ? (answerData.selectedChoice || null) : null,
    confidence: confidence || 'unsure'
  });

  // Build alerts
  const alerts = [];
  if (fatigueAlert) alerts.push(fatigueAlert);

  return {
    cardState: newCard,
    grade,
    masteryDeltas,
    calibrationUpdate,
    alerts,
    nextActionHints: alerts.length > 0
      ? [alerts[0].recommendedAction]
      : ['continue']
  };
}

/**
 * Get engine insight — AI-powered explanation via Firebase Cloud Function.
 *
 * @param {string} userId
 * @param {string} certId
 * @param {string} type - 'question' | 'misconception' | 'readiness'
 * @param {object} context - type-specific context data
 * @returns {Promise<{type, explanation, cached}>}
 */
export async function getEngineInsight(userId, certId, type, context) {
  return getInsight(userId, certId, type, context || {});
}

/**
 * Get full engine state for observability/debugging.
 * @param {string} certId - optional, returns specific cert state
 */
export function getEngineState(certId) {
  if (certId) {
    const ctx = _certContexts[certId];
    const graphStats = getGraphStats(certId);
    return {
      certId: certId,
      initialized: ctx ? ctx.initialized : false,
      questionCount: ctx ? ctx.questionBank.length : 0,
      catStates: getAllCatStates(certId),
      graphStats: graphStats,
      certSpec: ctx ? ctx.certSpec : null
    };
  }

  // Return all cert contexts
  const allStates = {};
  for (const [cid, ctx] of Object.entries(_certContexts)) {
    allStates[cid] = {
      initialized: ctx.initialized,
      questionCount: ctx.questionBank.length,
      catStates: getAllCatStates(cid),
      graphStats: getGraphStats(cid)
    };
  }
  return {
    storeInitialized: _storeInitialized,
    certs: allStates
  };
}

// Re-export session and profile functions
export { startSession, getNextQuestion, endSession };
export { getLearnerProfile };
export { getGraphStats, getConceptMastery, getWeakConcepts, computeCoverage, getCrossDomainLinks } from './knowledge-graph.js';
export { flushAnalytics, getDistractorFrequency, getReinforcementRecoveryRate, getSessionAnalytics } from './analytics.js';
export { forecastWeaknesses, getReinforcementTarget } from './weakness-forecast.js';
export { detectMisconceptions, getMisconceptionSummary } from './misconception.js';
export { getQuestionExplanation, getMisconceptionExplanation, getReadinessInsight, setExplanationCert } from './explanations.js';
export { loadTheta } from './selector.js';
